import {
  ClickIcon,
  CubeIcon,
  FrameStackIcon,
  GlobeIcon,
  type IconBoxIconAccessory,
  MailClickIcon,
  MetricIcon,
  type IconAccessory,
  TraitIcon,
} from "@hightouch/htui";
import isEqual from "lodash/isEqual";

import type {
  RelationshipFragment,
  TraitDefinitionFragment,
} from "src/graphql";
import type {
  Metric,
  MetricSelectionColumn,
  ParentModel,
} from "src/pages/analytics/types";
import { ColumnType, getInitialTraitColumn } from "src/types/visual";

import { columnReferenceKey } from "src/components/explore/filter-popover/utils";
import { MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES } from "src/components/explore/filter-popover/constants";
import { getTraitPropertyType } from "src/components/explore/visual/utils";
import { isRealtimeTrait } from "src/components/traits/utils";
import { getManagedModelChannel } from "src/cep/utils";
import { CHANNELS } from "src/cep/messages/constants";
import { ChannelType } from "src/types/cep";
import { getEventIdFromMetricSelection } from "src/pages/analytics/utils";
import { isPresent } from "ts-extras";
import type {
  MetricSelectValue,
  Option,
  OptionGroup,
  OptionMapAndFilterFunc,
} from "./types";
import type { GroupByColumn } from "@hightouch/lib/query/visual/types";

export const buildParentModelOptions = ({
  parentModelId,
  parentModelName,
}: {
  parentModelId: string | null;
  parentModelName: string | null;
}): Option[] => {
  if (!parentModelName || !parentModelId) return [];

  return [
    {
      type: "parent-model",
      label: "Total members",
      value: {
        modelId: parentModelId,
        modelName: parentModelName,
      },
      description: null,
    },
  ];
};

export const buildParentModelColumnOptions = ({
  parentModel,
  restrictMergedColumns = false,
}: {
  parentModel: ParentModel | null;
  restrictMergedColumns: boolean;
}): Option[] => {
  if (!parentModel) return [];
  const columns: Option[] = [];

  for (const column of parentModel.filterable_audience_columns.sort(
    columnSorter,
  )) {
    if (restrictMergedColumns && column.column_reference.type !== "raw") {
      continue;
    }

    const columnName = column.alias || column.name;
    const columnType = (column.custom_type ?? column.type) as ColumnType;

    columns.push({
      type: "parent-model-property",
      label: columnName,
      value: {
        modelId: String(parentModel.id), // NOTE: We want to refer to the parent model, even if the column is a related model
        modelName: column.model_name,
        column: column,
        columnName: columnName,
        columnType: columnType,
      },
      description: column.description,
    });
  }

  return columns;
};

// Models whose channel can't be derived fall into a trailing "Other" subheader.
const OTHER_MESSAGE_EVENTS_KEY = "other";

export const buildEventModelAndColumnOptions = (
  eventRelationships: RelationshipFragment[] | null,
): {
  eventModelOptions: Option[];
  eventModelColOptions: OptionGroup[];
  messageEventModelOptions: OptionGroup[];
  messageEventModelColOptions: OptionGroup[];
} => {
  const eventModelOptions: Option[] = [];
  const eventModelColOptions: OptionGroup[] = [];
  const messageEventModelColOptions: OptionGroup[] = [];
  // Message-event model options bucketed by channel ("email"/"sms"/"push"), plus
  // an "other" bucket for any model whose channel can't be derived.
  const messageEventOptionsByChannel = new Map<string, Option[]>();

  if (!eventRelationships)
    return {
      eventModelOptions,
      eventModelColOptions,
      messageEventModelOptions: [],
      messageEventModelColOptions,
    };

  eventRelationships.sort(relationshipSorter).forEach((relationship) => {
    const eventModel = relationship.to_model;
    // CEP managed models surface as a separate, channel-grouped "Message events"
    // category rather than mixing in with regular events.
    const isManagedModel = Boolean(eventModel.cep_managed_model);
    const channel = isManagedModel
      ? getManagedModelChannel(eventModel.cep_managed_model?.type)
      : undefined;

    const modelOption: Option = {
      type: isManagedModel ? "message-event-model" : "event-model",
      label: eventModel.name,
      channel,
      value: {
        modelName: eventModel.name,
        eventModelId: eventModel.id.toString(),
        relationshipId: relationship.id.toString(),
      },
      description: eventModel.description,
    };

    if (isManagedModel) {
      const key = channel ?? OTHER_MESSAGE_EVENTS_KEY;
      const bucket = messageEventOptionsByChannel.get(key) ?? [];
      bucket.push(modelOption);
      messageEventOptionsByChannel.set(key, bucket);
    } else {
      eventModelOptions.push(modelOption);
    }

    const colOptions: OptionGroup = {
      label: eventModel.name,
      options: eventModel.filterable_audience_columns
        .sort(columnSorter)
        .map((column) => {
          const columnName = column.alias || column.name;
          const columnType = (column.custom_type ?? column.type) as ColumnType;

          return {
            type: isManagedModel
              ? "message-event-model-property"
              : "event-model-property",
            label: columnName,
            channel,
            value: {
              modelName: eventModel.name,
              eventModelId: eventModel.id.toString(),
              relationshipId: relationship.id.toString(),
              column,
              columnName,
              columnType,
            },
            description: column.description,
          };
        }),
    };

    if (colOptions.options.length > 0) {
      (isManagedModel
        ? messageEventModelColOptions
        : eventModelColOptions
      ).push(colOptions);
    }
  });

  const messageEventModelOptions: OptionGroup[] = [
    ...Object.values(ChannelType).map((channelType) => ({
      label: CHANNELS[channelType].label,
      options: messageEventOptionsByChannel.get(channelType) ?? [],
    })),
    {
      label: "Other",
      options: messageEventOptionsByChannel.get(OTHER_MESSAGE_EVENTS_KEY) ?? [],
    },
  ].filter((group) => group.options.length > 0);

  return {
    eventModelOptions,
    eventModelColOptions,
    messageEventModelOptions,
    messageEventModelColOptions,
  };
};

export const buildRelatedModelAndColumnOptions = (
  relationsRelationships: RelationshipFragment[] | null,
): { relatedModelOptions: Option[]; relatedModelColOptions: OptionGroup[] } => {
  const relatedModelOptions: Option[] = [];
  const relatedModelColOptions: OptionGroup[] = [];

  if (!relationsRelationships)
    return { relatedModelOptions, relatedModelColOptions };

  relationsRelationships.sort(relationshipSorter).forEach((relationship) => {
    const relatedModel = relationship.to_model;

    relatedModelOptions.push({
      type: "related-model",
      label: relatedModel.name,
      value: {
        modelName: relatedModel.name,
        modelId: relatedModel.id.toString(),
        relationshipId: relationship.id.toString(),
      },
      description: relatedModel.description,
    });

    const colOptions: OptionGroup = {
      label: relatedModel.name,
      options: relatedModel.filterable_audience_columns
        .sort(columnSorter)
        .map((column) => {
          const columnName = column.alias || column.name;
          const columnType = (column.custom_type ?? column.type) as ColumnType;

          return {
            type: "related-model-property",
            label: columnName,
            value: {
              modelName: relatedModel.name,
              modelId: relatedModel.id.toString(),
              relationshipId: relationship.id.toString(),
              column,
              columnName,
              columnType,
            },
            description: column.description,
          };
        }),
    };

    if (colOptions.options.length > 0) {
      relatedModelColOptions.push(colOptions);
    }
  });

  return { relatedModelOptions, relatedModelColOptions };
};

export const buildMetricOptions = (
  metrics: (Metric | { name: string; id: string; description: null })[] | null,
): Option[] => {
  if (!metrics) return [];

  return metrics.sort(columnSorter).map((metric) => {
    return {
      type: "metric",
      label: metric.name,
      value: {
        metricId: metric.id,
        metricName: metric.name,
      },
      description: metric.description,
    };
  });
};

export const buildTraitOptions = ({
  traits,
  parentModelId,
  parentModelName,
}: {
  traits: TraitDefinitionFragment[] | null;
  parentModelId: string | null;
  parentModelName: string;
}): Option[] => {
  if (!traits || !parentModelId) return [];

  // Realtime traits aren't supported in analytics metrics/group-bys, so hide them.
  const warehouseTraits = traits.filter((trait) => !isRealtimeTrait(trait));

  return warehouseTraits.map((trait) => {
    const columnReference = getInitialTraitColumn(trait);
    const columnType = getTraitPropertyType(
      trait,
      trait.relationship?.to_model?.filterable_audience_columns,
    );

    // Similar to merged models, we want to show the model the trait
    // is calculated from. If there is no relationship, we use the parent model.
    const modelId: string = trait.relationship?.to_model.id ?? parentModelId;
    const modelName: string =
      trait.relationship?.to_model.name ?? parentModelName;

    const column: MetricSelectionColumn = {
      column_reference: columnReference,
      model_id: modelId,
      model_name: modelName,
      name: trait.name,
      type: columnType,
      description: trait.description,
      custom_type: null,
      alias: null,
    };

    return {
      type: "trait",
      label: trait.name,
      value: {
        traitId: trait.id,
        modelId: parentModelId,
        modelName: modelName,
        columnName: trait.name,
        columnType,
        column,
      },
      description: trait.description,
    };
  });
};

export const optionTypeToAccessory = (
  type: Option["type"] | "all",
): IconBoxIconAccessory => {
  switch (type) {
    case "event-model":
      return {
        type: "icon-box",
        icon: <ClickIcon />,
        color: "teal",
      };
    case "event-model-property":
      return {
        type: "icon-box",
        icon: <ClickIcon />,
        color: "teal",
        variant: "inverted",
      };
    case "message-event-model":
      return {
        type: "icon-box",
        icon: <MailClickIcon />,
        color: "orange",
      };
    case "message-event-model-property":
      return {
        type: "icon-box",
        icon: <MailClickIcon />,
        color: "orange",
        variant: "inverted",
      };
    case "parent-model":
      return {
        type: "icon-box",
        icon: <CubeIcon />,
        color: "indigo",
      };
    case "parent-model-property":
      return {
        type: "icon-box",
        icon: <CubeIcon />,
        color: "indigo",
        variant: "inverted",
      };
    case "related-model":
      return {
        type: "icon-box",
        icon: <FrameStackIcon />,
        color: "green",
      };
    case "related-model-property":
      return {
        type: "icon-box",
        icon: <FrameStackIcon />,
        color: "green",
        variant: "inverted",
      };
    case "trait":
      return {
        type: "icon-box",
        icon: <TraitIcon />,
        color: "yellow",
      };
    case "metric":
      return {
        type: "icon-box",
        icon: <MetricIcon />,
        color: "orange",
      };
    case "all":
      return {
        type: "icon-box",
        icon: <GlobeIcon />,
        color: "gray",
      };
    default:
      throw new Error(`Unknown type: ${type satisfies never}`);
  }
};

export const optionAccessory = (option: Option): IconAccessory => {
  // Message-event options use a per-channel icon (Email/SMS/Push), matching the
  // filter popover.
  if (
    option.channel &&
    (option.type === "message-event-model" ||
      option.type === "message-event-model-property")
  ) {
    const { icon, color } =
      MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES[option.channel];
    return {
      type: "icon-box",
      icon,
      color,
      ...(option.type === "message-event-model-property"
        ? { variant: "inverted" as const }
        : {}),
    };
  }

  return optionTypeToAccessory(option.type);
};

export const optionEquality = (
  optionValue: MetricSelectValue,
  value: MetricSelectValue,
) => {
  // Short circuit for metrics
  if (value.metricId) return optionValue.metricId === value.metricId;

  // Short circuit for traits
  if (value.traitId) return optionValue.traitId === value.traitId;

  const optionValueSubset = {
    traitId: optionValue.traitId ?? null,
    eventModelId: optionValue.eventModelId ?? null,
    modelId: optionValue.modelId ?? null,
    column: optionValue.column?.column_reference ?? null,
  };

  const valueSubset = {
    traitId: value.traitId ?? null,
    eventModelId: value.eventModelId ?? null,
    modelId: value.modelId ?? null,
    column: value.column?.column_reference ?? null,
  };

  return isEqual(optionValueSubset, valueSubset);
};

export const columnOptionEquality = (
  optionValue: MetricSelectValue,
  value: MetricSelectValue,
) => {
  const optionValueSubset = {
    column: optionValue.column?.column_reference ?? null,
  };

  const valueSubset = {
    column: value.column?.column_reference ?? null,
  };

  return isEqual(optionValueSubset, valueSubset);
};

export const emptyOptionGroupFilter = (optionGroup: { options: any[] }) =>
  optionGroup.options.length > 0;

export const stringOptionFilter = (option: Option) =>
  option.value.columnType === ColumnType.String;

export const stringOrBooleanOptionFilter = (option: Option) =>
  option.value.columnType === ColumnType.String ||
  option.value.columnType === ColumnType.Boolean;

export const columnSorter = (
  a: {
    // Alias is optional to support saved metrics
    alias?: MetricSelectionColumn["alias"];
    name: MetricSelectionColumn["name"];
  },
  b: {
    alias?: MetricSelectionColumn["alias"];
    name: MetricSelectionColumn["name"];
  },
) => `${a.alias || a.name}`.localeCompare(`${b.alias || b.name}`);

const relationshipSorter = (
  a: { to_model: { name: string } },
  b: { to_model: { name: string } },
) => `${a.to_model.name}`.localeCompare(`${b.to_model.name}`);

/**
 * Filters the options for a related model column based on the metric model id
 * @param metric - The metric to filter
 * @param columns - Optional. The columns to filter by. If provided, the options will be filtered by the columns.
 * @param metricDefinitions - Optional. The metric definitions to fetch the event id from for saved metrics
 * @returns A function that filters the options for a related model column based on the metric
 */
export const mapAndFilterColOptions = ({
  metrics,
  columns,
  metricDefinitions, // Allows us to fetch the event associated with a saved metric
  onlyStringOptions = false,
}: {
  metrics: MetricSelectValue[];
  columns?: GroupByColumn[];
  metricDefinitions?: Metric[];
  onlyStringOptions?: boolean;
}): OptionMapAndFilterFunc => {
  // Pre-build the column-reference Set once per factory invocation so the
  // filter inside filterRelatedModelColOptions is O(options) instead of
  // O(options × columns × deepEq)
  const columnKeys = columns
    ? new Set(
        columns.map(({ buckets: _buckets, ...column }) =>
          columnReferenceKey(column),
        ),
      )
    : undefined;

  return (optionGroup: OptionGroup) => {
    const initialOption = optionGroup.options[0];
    if (!initialOption) return optionGroup;

    // If the metric is a saved metric, we need to get the event id from the metric definition
    const metricModelIds = metrics
      .map((metric) => getMetricModelId({ metric, metricDefinitions }))
      .filter(isPresent);

    const options = filterRelatedModelColOptions({
      metricModelIds,
      optionGroup,
      columnKeys,
      onlyStringOptions,
    });

    return {
      ...optionGroup,
      options,
    };
  };
};

const getMetricModelId = ({
  metric,
  metricDefinitions,
}: {
  metric: MetricSelectValue;
  metricDefinitions?: Metric[];
}) => {
  return metric.metricId
    ? getEventIdFromMetricSelection(
        {
          id: metric.metricId,
          eventModelId: null,
        },
        metricDefinitions ?? [],
      )?.toString()
    : metric.modelId || metric.eventModelId || null;
};

/**
 * Filters the options for a related model column based on the metric model id
 * @param metricModelId - The model id of the metric
 * @param columnKeys - Optional. The pre-built Set of stable column-reference keys to filter by. If provided, the options will be filtered to those whose column reference appears in the Set.
 * @param optionGroup - The option group to filter
 * @returns The filtered options
 */
const filterRelatedModelColOptions = ({
  metricModelIds,
  columnKeys,
  optionGroup,
  onlyStringOptions = false,
}: {
  metricModelIds: string[];
  optionGroup: OptionGroup;
  columnKeys?: Set<string>;
  onlyStringOptions?: boolean;
}): Option[] => {
  if (metricModelIds.length === 0 && !columnKeys) return [];

  const initialOption = optionGroup.options[0];
  const optionModelId =
    initialOption?.value.modelId || initialOption?.value.eventModelId;

  // If the option modelId does not exist, return an empty array
  if (!optionModelId) return [];

  // If the option modelId does not match the metric modelId, we don't want to show the options
  if (metricModelIds.includes(optionModelId)) {
    return onlyStringOptions
      ? optionGroup.options.filter(stringOptionFilter)
      : optionGroup.options;
  }

  // If the columns are provided, we want to filter the options by the columns
  // This is useful when you don't want to return the entire set of columns
  if (columnKeys) {
    return optionGroup.options.filter((option) => {
      const optionColumn = option.value.column?.column_reference;
      if (!optionColumn) return false;
      return columnKeys.has(columnReferenceKey(optionColumn));
    });
  }

  return [];
};
